import {
    View, Grid, GridColumn, Page, colors, Link
} from '@fairygodboss/ui';
import { NextPage } from 'next';
import Head from 'next/head';
import React, { useEffect, useState } from 'react';

import { GoogleMetricTracer } from '../../utils/Analytics/Google/GoogleMetricTracer';
import { SnowplowPageViewTracker } from '../../utils/Analytics/SnowPlow/SnowPlowPageViewtracker';
import { EmitPage } from '../../utils/Analytics/VisibilityPixel/VisibilityPixel';
import useUser from '../../utils/useUser';

import styles from './CookiePolicy.module.scss';

import { Footer } from 'components/FooterV2';

const CookiePolicy: NextPage = () => {
    const [isMounted, setIsMounted] = useState(false);
    const { user, userLoading, statusLoading } = useUser();
    useEffect(() => {
        setIsMounted(true);
    }, []);

    useEffect(() => {
        if (!userLoading) EmitPage({ user_id: user.id || null, user_email: user.email });
    }, [user, userLoading]);

    return (
        <>
            <Head>
                <title>Cookie Policy | Fairygodboss</title>
                <meta
                    property="og:url"
                    content="https://fairygodboss.com/cookie-policy"
                />
                <meta
                    property="og:description"
                    content="Fairygodboss's cookie policy."
                />
                <meta
                    name="description"
                    content="Fairygodboss's cookie policy."
                />
                <meta
                    property="og:title"
                    content="Cookie Policy | Fairygodboss"
                />
                <link
                    rel="canonical"
                    href="https://fairygodboss.com/cookie-policy"
                />
            </Head>
            <SnowplowPageViewTracker />
            {isMounted && !userLoading && !statusLoading && (
                <>
                    <GoogleMetricTracer
                        user={user}
                        pagePath="/cookie-policy"
                        pageTitle="Cookie Policy | Fairygodboss"
                    />
                </>
            )}
            <View
                className={styles['page-PrivacyPolicy-CookiesPolicy']}
                display="flex"
                width="100%"
                color={colors.neutral[100]}
                background="#F2F2F2"
                css={{
                    fontFamily: 'Open Sans',
                }}
            >
                <View display="flex" width="100%" flexDirection="column">
                    <Page width="100%" className={styles['main-page-section']}>
                        <Grid
                            maxWidth="1176px"
                            background="white"
                            mt={{ _: '18px' }}
                            padding={{ _: '0px 10px', lg: '24px 48px' }}
                        >
                            <GridColumn
                                mt={{ _: '18px' }}
                                size={{ _: 11, lg: 11.5 }}
                                textAlign="center"
                            >
                                <h2 className={styles['main-headline']}>
                                    Cookie Policy
                                </h2>
                                <p className={styles['effective-date']}>
                                    <strong>
                                        Last Updated: March 27th, 2024
                                    </strong>
                                </p>
                            </GridColumn>
                            <GridColumn
                                size={{
                                    xs: 12,
                                    sm: 9,
                                    md: 12,
                                    lg: 11,
                                }}
                                pl={{ _: '0px', md: '24px', lg: '8px' }}
                                css={{
                                    maxWidth: '100%',
                                }}
                            >
                                <p>
                                    This Cookie Policy explains how Daily Muse,
                                    Inc. (&ldquo;
                                    <strong>The Muse</strong>
                                    &rdquo;, &ldquo;
                                    <strong>Fairygodboss</strong>
                                    &rdquo;, &ldquo;
                                    <strong>we</strong>
                                    &rdquo;, &ldquo;
                                    <strong>us</strong>
                                    &rdquo; or &ldquo;
                                    <strong>our</strong>
                                    &rdquo;) uses cookies and similar
                                    technologies in connection with the
                                    {' '}
                                    <Link href="/" target="_self">
                                        www.fairygodboss.com
                                    </Link>
                                    {' '}
                                    <Link
                                        href="https://www.themuse.com"
                                        target="_self"
                                    >
                                        www.themuse.com
                                    </Link>
                                    {' '}
                                    website and any other website that we own or
                                    control and which posts or links to this
                                    Cookie Policy (collectively, the &ldquo;
                                    <strong>Sites</strong>
                                    &rdquo;).
                                </p>
                                <p>
                                    <strong>What are cookies?</strong>
                                </p>
                                <p>
                                    Cookies are small data files that are placed
                                    on your computer or mobile device when you
                                    visit a website. Cookies serve different
                                    purposes, like helping us understand how a
                                    site is being used, letting you navigate
                                    between pages efficiently, remembering your
                                    preferences and generally improving your
                                    browsing experience.
                                </p>
                                <p>
                                    Our Sites may use both session cookies
                                    (which expire once you close your web
                                    browser) and persistent cookies (which stay
                                    on your computer or mobile device until you
                                    delete them).
                                </p>
                                <p>
                                    We use two broad categories of cookies: (1)
                                    first party cookies, served directly by us
                                    to your computer or mobile device, which we
                                    use to recognize your computer or mobile
                                    device when it revisits our Sites; and (2)
                                    third party cookies, which are served by
                                    service providers or business partners on
                                    our Sites, and can be used by these parties
                                    to recognize your computer or mobile device
                                    when it visits other websites. Third party
                                    cookies can be used for a variety of
                                    purposes, including site analytics and
                                    social media features.
                                </p>
                                <p>
                                    <strong>
                                        What types of cookies and similar
                                        tracking technologies does Fairygodboss
                                        use on the Sites?
                                    </strong>
                                </p>
                                <p>
                                    On the Sites, we use cookies and other
                                    tracking technologies in the following
                                    categories described
                                    {' '}
                                    <View display={{ _: 'none', md: 'inline' }}>
                                        {' '}
                                        in the table
                                        {' '}
                                    </View>
                                    {' '}
                                    below.
                                </p>

                                <View
                                    display={{ _: 'block', md: 'none' }}
                                    borderBottom="1px solid"
                                >
                                    <div>
                                        <h5> Analytics </h5>
                                        <ul>
                                            <li>
                                                {' '}
                                                <strong>
                                                    Description:
                                                </strong>
                                                {' '}
                                                These cookies help us understand
                                                how our Service is performing
                                                and being used. These cookies
                                                may work with web beacons
                                                included in emails we send to
                                                track which emails are opened
                                                and which links are clicked by
                                                recipients.
                                            </li>

                                            <li>
                                                {' '}
                                                <strong>
                                                    Who serves the cookies:
                                                </strong>
                                                {' '}
                                                <Link href="https://policies.google.com/privacy?hl=en">
                                                    Google Analytics
                                                </Link>
                                            </li>
                                            <li>
                                                {' '}
                                                <strong>
                                                    How to control them:
                                                    {' '}
                                                </strong>
                                                See &apos;your choices&apos;
                                                below.
                                                <p>
                                                    Google Analytics uses its
                                                    own cookies. You can find
                                                    out more information about
                                                    Google Analytics
                                                    cookies&nbsp;
                                                    <Link href="https://developers.google.com/analytics/resources/concepts/gaConceptsCookies">
                                                        here
                                                    </Link>
                                                    &nbsp; and about how Google
                                                    protects your data&nbsp;
                                                    <Link href="https://www.google.com/analytics/learn/privacy.html">
                                                        here
                                                    </Link>
                                                    . You can prevent the use of
                                                    Google Analytics relating to
                                                    your use of our Sites by
                                                    downloading and installing a
                                                    browser plugin
                                                    available&nbsp;
                                                    <Link href="https://tools.google.com/dlpage/gaoptout?hl=en-GB">
                                                        here
                                                    </Link>
                                                    .
                                                </p>
                                            </li>
                                        </ul>
                                    </div>
                                    <div>
                                        <h5>Essential </h5>
                                        <ul>
                                            <li>
                                                {' '}
                                                <strong>
                                                    Description:
                                                </strong>
                                                {' '}
                                                These cookies are necessary to
                                                allow the technical operation of
                                                our Service (e.g., they enable
                                                you to move around on a website
                                                and to use its features)
                                            </li>

                                            <li>
                                                {' '}
                                                <strong>
                                                    Who serves the cookies:
                                                </strong>
                                                {' '}
                                                <Link href="https://policies.google.com/privacy?hl=en">
                                                    Google Tag Manager
                                                </Link>
                                            </li>
                                            <li>
                                                {' '}
                                                <strong>
                                                    How to control them:
                                                    {' '}
                                                </strong>
                                                See &apos;your choices&apos;
                                                below.
                                            </li>
                                        </ul>
                                    </div>
                                    <div>
                                        <h5>Functionality/performance </h5>
                                        <ul>
                                            <li>
                                                {' '}
                                                <strong>
                                                    Description:
                                                </strong>
                                                {' '}
                                                Enhance the performance and
                                                functionality of our Service.
                                            </li>

                                            <li>
                                                {' '}
                                                <strong>
                                                    Who serves the cookies:
                                                </strong>
                                                {' '}
                                                <Link href="https://www.intercom.com/terms-and-policies#privacy">
                                                    Intercom
                                                </Link>
                                            </li>
                                            <li>
                                                {' '}
                                                <strong>
                                                    How to control them:
                                                    {' '}
                                                </strong>
                                                See &apos;your choices&apos;
                                                below.
                                            </li>
                                        </ul>
                                    </div>
                                </View>

                                <View
                                    className={styles['cookie-types-box']}
                                    display={{ _: 'none', md: 'block' }}
                                >
                                    <Grid
                                        className={`${styles.row} ${styles['box-heading']}`}
                                        flexDirection="row"
                                        width="100%"
                                        ml={{ _: '0px !important' }}
                                        mr={{ _: '0px !important' }}
                                    >
                                        <GridColumn
                                            size={{ _: 3 }}
                                            className={styles.column}
                                        >
                                            <strong>Type</strong>
                                        </GridColumn>
                                        <GridColumn
                                            size={{ _: 3 }}
                                            className={styles.column}
                                        >
                                            <strong>Description</strong>
                                        </GridColumn>
                                        <GridColumn
                                            size={{ _: 3 }}
                                            className={styles.column}
                                        >
                                            <strong>
                                                Who serves the cookies
                                            </strong>
                                        </GridColumn>
                                        <GridColumn
                                            size={{ _: 3 }}
                                            className={styles.column}
                                        >
                                            <strong>How to control them</strong>
                                        </GridColumn>
                                    </Grid>

                                    <Grid
                                        flexDirection="row"
                                        className={`${styles.row}`}
                                        ml={{ _: '0px !important' }}
                                        mr={{ _: '0px !important' }}
                                    >
                                        <GridColumn
                                            size={{ _: 3 }}
                                            className={styles.column}
                                        >
                                            <p>
                                                <em>Analytics</em>
                                            </p>
                                        </GridColumn>
                                        <GridColumn
                                            size={{ _: 3 }}
                                            className={styles.column}
                                        >
                                            <p>
                                                These cookies help us understand
                                                how our Service is performing
                                                and being used. These cookies
                                                may work with web beacons
                                                included in emails we send to
                                                track which emails are opened
                                                and which links are clicked by
                                                recipients.
                                            </p>
                                        </GridColumn>
                                        <GridColumn
                                            size={{ _: 3 }}
                                            className={styles.column}
                                        >
                                            <div>
                                                <Link href="https://policies.google.com/privacy?hl=en">
                                                    Google Analytics
                                                </Link>
                                            </div>
                                        </GridColumn>
                                        <GridColumn
                                            size={{ _: 3 }}
                                            className={styles.column}
                                        >
                                            <p>
                                                See &apos;your choices&apos;
                                                below.
                                            </p>
                                            <p>
                                                Google Analytics uses its own
                                                cookies. You can find out more
                                                information about Google
                                                Analytics cookies&nbsp;
                                                <Link href="https://developers.google.com/analytics/resources/concepts/gaConceptsCookies">
                                                    here
                                                </Link>
                                                &nbsp; and about how Google
                                                protects your data&nbsp;
                                                <Link href="https://www.google.com/analytics/learn/privacy.html">
                                                    here
                                                </Link>
                                                . You can prevent the use of
                                                Google Analytics relating to
                                                your use of our Sites by
                                                downloading and installing a
                                                browser plugin available&nbsp;
                                                <Link href="https://tools.google.com/dlpage/gaoptout?hl=en-GB">
                                                    here
                                                </Link>
                                                .
                                            </p>
                                        </GridColumn>
                                    </Grid>

                                    <Grid
                                        className={styles.row}
                                        ml={{ _: '0px !important' }}
                                        mr={{ _: '0px !important' }}
                                    >
                                        <GridColumn
                                            size={{ _: 3 }}
                                            className={styles.column}
                                        >
                                            <p>
                                                <em>Essential</em>
                                            </p>
                                        </GridColumn>
                                        <GridColumn
                                            size={{ _: 3 }}
                                            className={styles.column}
                                        >
                                            <p>
                                                These cookies are necessary to
                                                allow the technical operation of
                                                our Service (e.g., they enable
                                                you to move around on a website
                                                and to use its features).
                                            </p>
                                        </GridColumn>
                                        <GridColumn
                                            size={{ _: 3 }}
                                            className={styles.column}
                                        >
                                            <p>
                                                <Link href="https://policies.google.com/privacy?hl=en">
                                                    Google Tag Manager
                                                </Link>
                                            </p>
                                        </GridColumn>
                                        <GridColumn
                                            size={{ _: 3 }}
                                            className={styles.column}
                                        >
                                            <p>
                                                See &apos;your choices&apos;
                                                below.
                                            </p>
                                        </GridColumn>
                                    </Grid>

                                    <Grid
                                        className={styles.row}
                                        ml={{ _: '0px !important' }}
                                        mr={{ _: '0px !important' }}
                                    >
                                        <GridColumn
                                            size={{ _: 3 }}
                                            className={styles.column}
                                        >
                                            <p>
                                                <em>
                                                    Functionality/performance
                                                </em>
                                            </p>
                                        </GridColumn>
                                        <GridColumn
                                            size={{ _: 3 }}
                                            className={styles.column}
                                        >
                                            <p>
                                                Enhance the performance and
                                                functionality of our Service.
                                            </p>
                                        </GridColumn>
                                        <GridColumn
                                            size={{ _: 3 }}
                                            className={styles.column}
                                        >
                                            <p>
                                                <Link href="https://www.intercom.com/terms-and-policies#privacy">
                                                    Intercom
                                                </Link>
                                            </p>
                                        </GridColumn>
                                        <GridColumn
                                            size={{ _: 3 }}
                                            className={styles.column}
                                        >
                                            <p>
                                                See &apos;your choices&apos;
                                                below.
                                            </p>
                                        </GridColumn>
                                    </Grid>
                                </View>
                                <p>
                                    <strong>Other technologies</strong>
                                </p>
                                <p>
                                    In addition to cookies, our Sites may use
                                    other technologies, such as Flash technology
                                    to pixel tags to collect information
                                    automatically.
                                </p>
                                <p>
                                    <em>Browser Web Storage</em>
                                </p>
                                <p>
                                    We may use browser web storage (including
                                    via HTML5), also known as locally stored
                                    objects (“LSOs”), for similar purposes as
                                    cookies. Browser web storage enables the
                                    storage of a larger amount of data than
                                    cookies. Your web browser may provide
                                    functionality to clear your browser web
                                    storage.
                                </p>
                                <p>
                                    <em>Flash Technology</em>
                                </p>
                                <p>
                                    We may use Flash cookies (which are also
                                    known as Flash Local Shared Object (“Flash
                                    LSOs”)) on our Sites to collect and store
                                    information about your use of our Sites.
                                    Unlike other cookies, Flash cookies cannot
                                    be removed or rejected via your browser
                                    settings. If you do not want Flash LSOs
                                    stored on your computer or mobile device,
                                    you can adjust the settings of your Flash
                                    player to block Flash LSO storage using the
                                    tools contained in the Website Storage
                                    Settings Panel. You can also control Flash
                                    LSOs by going to the Global Storage Settings
                                    Panel and following the instructions. Please
                                    note that setting the Flash Player to
                                    restrict or limit acceptance of Flash LSOs
                                    may reduce or impede the functionality of
                                    some Flash applications, including,
                                    potentially, Flash applications used in
                                    connection with our Sites.
                                </p>
                                <p>
                                    <em>Web Beacons</em>
                                </p>

                                <p>
                                    We may also use web beacons (which are also
                                    known as pixel tags and clear GIFs) on our
                                    Sites and in our HTML formatted emails to
                                    track the actions of users on our Sites and
                                    interactions with our emails. Unlike
                                    cookies, which are stored on the hard drive
                                    of your computer or mobile device by a
                                    website, pixel tags are embedded invisibly
                                    on webpages or within HTML formatted emails.
                                    Pixel tags are used to demonstrate that a
                                    webpage was accessed or that certain content
                                    was viewed, typically to measure the success
                                    of our marketing campaigns or engagement
                                    with our emails and to compile statistics
                                    about usage of the Sites, so that we can
                                    manage our content more effectively.
                                </p>

                                <p>
                                    <strong>Your choices</strong>
                                </p>

                                <p>
                                    Most browsers let you remove or reject
                                    cookies. To do this, follow the instructions
                                    in your browser settings. Many browsers
                                    accept cookies by default until you change
                                    your settings. Please note that if you set
                                    your browser to disable cookies, the Sites
                                    may not work properly.
                                </p>

                                <p>
                                    For more information about cookies,
                                    including how to see what cookies have been
                                    set on your computer or mobile device and
                                    how to manage and delete them, visit&nbsp;
                                    <Link href="https://www.allaboutcookies.org">
                                        www.allaboutcookies.org
                                    </Link>
                                    .
                                </p>
                                <p>
                                    If you do not accept our cookies, you may
                                    experience some inconvenience in your use of
                                    our Sites. For example, we may not be able
                                    to recognize your computer or mobile device
                                    and you may need to log in every time you
                                    visit our Sites.
                                </p>
                                <p>
                                    For more information about how we collect,
                                    use and share your information, see our
                                    Privacy Policy.
                                </p>

                                <p>
                                    <strong>Changes</strong>
                                </p>
                                <p>
                                    Information about the cookies we use may be
                                    updated from time to time, so please check
                                    back on a regular basis for any changes.
                                </p>
                                <p>
                                    <strong>Questions</strong>
                                </p>

                                <p>
                                    If you have any questions about this Cookie
                                    Policy, please contact us by email at&nbsp;
                                    <Link href="mailto:info@fairygodboss.com">
                                        info@fairygodboss.com
                                    </Link>
                                    .
                                </p>
                            </GridColumn>
                        </Grid>
                    </Page>
                    <Footer />
                </View>
            </View>
        </>
    );
};

export default CookiePolicy;
